import React from 'react';
import { Check } from '@phosphor-icons/react';
import classNames from 'classnames';

import { ButtonType, SpinnerSize, Placement } from '../../types';

import { getBtnClass, ButtonVariant, ButtonColour } from './getBtnClass';
import { Spinner, Popover, Tooltip } from '.';

interface Props {
  children: React.ReactNode;
  id?: string;
  onClick?: any;
  type?: ButtonType;

  // styles
  variant?: ButtonVariant;
  colour?: ButtonColour;
  withGap?: boolean;
  className?: string;

  // disabled
  disabled?: boolean;
  allowPointerEvents?: boolean;

  // submit
  isSubmitBtn?: boolean;
  isProcessing?: boolean;
  isDone?: boolean;
  spinner?: { size?: SpinnerSize; color?: string; hoverColor?: string };

  // popover & tooltip
  popover?: {
    header?: string;
    body?: string;
    placement?: Placement;
    hide?: boolean;
  };
  tooltip?: { text?: string | React.ReactNode; hide?: boolean };
}

const Button = (props: Props) => {
  const {
    children,
    id,
    onClick = () => {},
    type = undefined,
    variant = 'primary',
    colour = 'primary',
    withGap = false,
    className = '',
    disabled = false,
    allowPointerEvents = false,
    isSubmitBtn = false,
    isProcessing = false,
    isDone = false,
    spinner,
    popover,
    tooltip,
  } = props;

  const btnClass = classNames(
    getBtnClass(variant, colour, withGap),
    { 'disabled:pointer-events-none': !allowPointerEvents },
    className,
  );

  const btn = (
    <button
      id={id}
      type={type ?? (isSubmitBtn ? 'submit' : 'button')}
      className={btnClass}
      onClick={onClick}
      disabled={disabled || isProcessing}
      data-enabled={!disabled && !isProcessing}
    >
      {isSubmitBtn ? (
        <React.Fragment>
          <div
            className={classNames({
              'transition-opacity duration-500 leading-none': true,
              'flex items-center gap-2': withGap,
              'opacity-100': !isProcessing && !isDone,
              'opacity-0': isProcessing || isDone,
            })}
          >
            {children}
          </div>

          <Spinner
            size={spinner?.size ?? 'lg'}
            color={spinner?.color ?? 'white'}
            hoverColor={spinner?.hoverColor ?? ''}
            className={classNames(
              'absolute transition-opacity duration-500',
              isProcessing ? 'opacity-100' : 'opacity-0',
            )}
          />

          <Check
            className={classNames(
              'absolute transition-opacity duration-500',
              isDone ? 'opacity-100' : 'opacity-0',
            )}
            size="1.25rem"
          />
        </React.Fragment>
      ) : (
        children
      )}
    </button>
  );

  if (popover?.header && popover?.body)
    return (
      <Popover
        placement={popover.placement}
        header={popover.header}
        body={popover.body}
        hide={popover.hide}
      >
        {btn}
      </Popover>
    );

  if (tooltip?.text)
    return (
      <Tooltip text={tooltip.text} hide={tooltip.hide}>
        {btn}
      </Tooltip>
    );

  return btn;
};

export default Button;
